import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useMemo, useState } from 'react';
import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
    ArrowDown2,
    ArrowRight2,
    ArrowUp2,
    Send2,
    Sparkles,
    Book1,
    ExportCurve,
    GripVertical,
    type IconComponent,
} from '@/components/icons';
import PatientAssignModal from '@/components/patients/PatientAssignModal';
import { getBlockRealId } from '@/components/preview/block-selection/blockClipboard';
import {
    UNSCOPED_GROUP_LABEL,
    listBlockGroups,
} from '@/components/preview/block-selection/blockGroups';
import { findBlockElement, stripSplitBlockId } from '@/components/preview/pagination/utils';
import { PublisherResultImportModal } from '@/components/publisher-results/PublisherResultImportModal';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AIBadge } from '@/components/ui/ai-badge';
import { useModalContext } from '@/components/ui/modal-provider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCursorGlow } from '@/hooks/use-cursor-glow';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { usePendingResultsReview } from './PendingResultsReviewModal';
import AddInstrumentMenu from './test-blocks/AddInstrumentMenu';
import BlockActionsPanel from './test-blocks/BlockActionsPanel';
import BlockToolboxPanel from './test-blocks/BlockToolboxPanel';
import type { WizardStepProps } from './types';

/** How many instruments the battery list shows before "Mostrar mais". */
const VISIBLE_GROUPS_COLLAPSED = 5;

/** `title` is ours (the heading), not the DOM tooltip attribute. */
type ResultSourceCardProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> & {
    icon: IconComponent;
    title: string;
    description?: ReactNode;
    showAIBadge?: boolean;
    /** Cursor-following glow on hover (default: true) */
    showGlow?: boolean;
};

/**
 * forwardRef + prop spread so the card can be a `DropdownMenuTrigger asChild`
 * (same shape as `PlaceholderBlock`): Radix has to reach the real button to
 * attach its ref and its `aria-expanded`/`data-state`.
 */
const ResultSourceCard = forwardRef<HTMLButtonElement, ResultSourceCardProps>(
    function ResultSourceCard(
        {
            icon: Icon,
            title,
            description,
            showAIBadge = false,
            showGlow = true,
            disabled = false,
            onClick,
            children,
            ...props
        },
        ref,
    ) {
        const hasEndAdornment = showAIBadge || Boolean(children);
        const glowRef = useCursorGlow({ disabled: disabled || !showGlow });

        return (
            <div ref={glowRef} className="relative">
                {showGlow && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -inset-3 rounded-3xl transition-opacity duration-300"
                        style={{
                            background:
                                'radial-gradient(circle var(--glow-size) at var(--glow-x) var(--glow-y), var(--glow-color), transparent)',
                            opacity: 'var(--glow-opacity)',
                            filter: 'blur(var(--glow-blur))',
                        }}
                    />
                )}
                <button
                    type="button"
                    ref={ref}
                    onClick={onClick}
                    disabled={disabled}
                    aria-disabled={disabled}
                    className={`relative z-10 w-full p-2 text-left transition-all ${
                        disabled
                            ? 'cursor-not-allowed rounded-2xl border-2 border-border bg-surface opacity-50'
                            : 'rounded-2xl border-2 border-border bg-card hover:bg-surface'
                    }`}
                    {...props}
                >
                    {showAIBadge && <AIBadge className="absolute right-3 top-3" />}
                    {children}
                    <div className="flex items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface text-muted-foreground">
                            <Icon className="size-5" />
                        </div>
                        <div className={hasEndAdornment ? 'pr-12' : undefined}>
                            <h4 className="text-sm font-medium text-foreground">{title}</h4>
                            {description && (
                                <div className="text-xs text-muted-foreground">{description}</div>
                            )}
                        </div>
                    </div>
                </button>
            </div>
        );
    },
);

export default function TestScoresStep({ onAddBlock, data }: WizardStepProps) {
    const { openWizardModal, closeModal } = useModalContext();
    const queryClient = useQueryClient();
    const {
        reportId,
        isReadOnly,
        pendingConsolidations,
        pendingPublisherResultApprovalImports,
        preparePublisherResultApproval,
        handlePublisherResultApproved,
        panelScope,
        setPanelScope,
        setSpotlightAnchorId,
    } = useReportWizard();
    const { openPendingResultsReview } = usePendingResultsReview(data);
    const selectedPatient = data?.patient;
    const hasSelectedPatient = Boolean(selectedPatient?.id);
    const activeReportId = reportId || data?.id || null;
    const sendTestAccess = useFeatureEntitlement('tests.send');
    const correctWithAiAccess = useFeatureEntitlement('tests.correctWithAi');
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');
    const pendingReviewCount =
        pendingConsolidations.length +
        (publisherResultImportsFeature.enabled ? pendingPublisherResultApprovalImports.length : 0);

    const invalidatePublisherResultImports = () => {
        queryClient.invalidateQueries({ queryKey: ['publisher-result-imports'] });
    };

    const handleAddInstrument = (toolId: string) => {
        if (!onAddBlock) return;

        onAddBlock(toolId);
    };

    const handleAssignInstruments = () => {
        if (!selectedPatient?.id) return;
        if (sendTestAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendTestAccess.decision));
            return;
        }

        openWizardModal({
            content: <PatientAssignModal patient={selectedPatient} mode="tests" fromReport />,
            size: 'lg',
            className: 'max-h-[90vh]',
        });
    };

    const handleImportPublisherResults = () => {
        if (!publisherResultImportsFeature.enabled) return;

        if (!selectedPatient?.id) {
            toast.warning('Selecione um paciente antes de importar resultados.');
            return;
        }

        if (!activeReportId) {
            toast.warning('Salve o laudo antes de importar resultados em PDF.');
            return;
        }

        if (isReadOnly) {
            toast.warning('Laudos concluídos não podem receber resultados importados.');
            return;
        }

        openWizardModal({
            title: null,
            size: 'xl',
            className: 'max-h-[95vh]',
            showCloseButton: false,
            content: (
                <PublisherResultImportModal
                    patientId={selectedPatient.id}
                    mode="report"
                    sourceReportId={activeReportId}
                    reportData={data}
                    onBeforeApprove={preparePublisherResultApproval}
                    onApproved={handlePublisherResultApproved}
                    onCompleted={invalidatePublisherResultImports}
                    onClose={closeModal}
                />
            ),
        });
    };

    const blocks = useMemo(() => data?.tests?.blocks ?? [], [data?.tests?.blocks]);

    // The block whose actions the panel shows. Resolved from the live block
    // list rather than cached on the scope, so an edit or a reorder cannot
    // leave the panel acting on a stale copy.
    const focusedBlock = useMemo(() => {
        if (!panelScope?.focusedRealId) return null;
        return (
            blocks.find(
                (block) => stripSplitBlockId(getBlockRealId(block)) === panelScope.focusedRealId,
            ) ?? null
        );
    }, [blocks, panelScope?.focusedRealId]);

    const batteryGroups = useMemo(() => listBlockGroups(blocks), [blocks]);

    // A long battery used to push the rest of the panel — the toolbox entry
    // points and the anchorless escape hatch — below the fold.
    const [showAllGroups, setShowAllGroups] = useState(false);
    const visibleGroups = showAllGroups
        ? batteryGroups
        : batteryGroups.slice(0, VISIBLE_GROUPS_COLLAPSED);

    // A single instrument has nowhere to go: no grip, no dead affordance.
    const canReorderBattery = !isReadOnly && batteryGroups.length > 1;

    const handleOpenGroup = (group: (typeof batteryGroups)[number]) => {
        setPanelScope({
            anchorRealId: group.anchorRealId,
            label: group.label,
            focusedRealId: stripSplitBlockId(group.firstRealId),
        });

        // The only thing that lights the instrument up in the laudo. Asking
        // "where is this one" is a deliberate gesture, so the mark is too: the
        // next pointerdown in the preview puts it out.
        setSpotlightAnchorId(group.anchorRealId);

        // The panel is about to talk about a group that may be off screen.
        // `findBlockElement` is split-aware: a paginated first block carries a
        // `__split__` suffix on its attribute, which an exact match misses.
        const previewContainer = document.querySelector('[data-preview-container]');
        if (previewContainer) {
            findBlockElement(previewContainer, group.firstRealId)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    // Level 1 only. Drilled in, the panel is about one instrument, and a card
    // that starts a different one competed with the actions for that instrument
    // — "Voltar" is one click away and the entry point is the first thing there.
    const instrumentEntryCard = (
        <AddInstrumentMenu surface="panel" onSelect={handleAddInstrument} disabled={isReadOnly}>
            <ResultSourceCard
                icon={Book1}
                title="Adicionar instrumento"
                showGlow={false}
                disabled={isReadOnly}
            />
        </AddInstrumentMenu>
    );

    // Drilled in: the panel answers "what can I do with the block I just
    // clicked" and "what goes with this instrument", in that order. Actions
    // come first because they are the half that had no visible home before —
    // the tiles were at least always on screen.
    if (panelScope) {
        return (
            <div className="space-y-6">
                {focusedBlock && (
                    <BlockActionsPanel
                        block={focusedBlock}
                        blocks={blocks}
                        onAddBlock={onAddBlock}
                    />
                )}

                <div className={focusedBlock ? 'border-t border-border pt-6' : undefined}>
                    {/* The actions half of a scoped table already says "Gerar
                        gráfico deste instrumento", and the tile below it landed
                        the very same block — the word "Gráfico" appeared twice
                        in one panel for one action. The named entry wins; the
                        tile comes back for every other kind of focus, where
                        nothing above it offers a chart. */}
                    <BlockToolboxPanel
                        scope={panelScope}
                        onAddBlock={onAddBlock}
                        hiddenToolIds={
                            focusedBlock?.block_type === 'table' ? ['tool_chart'] : undefined
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {pendingReviewCount > 0 && (
                <button
                    type="button"
                    onClick={openPendingResultsReview}
                    className="flex w-full items-center gap-2 rounded-lg border border-info/30 bg-info-subtle/60 px-3 py-2 text-left transition-colors hover:border-info/50 hover:bg-info-subtle"
                >
                    <Sparkles className="size-3.5 shrink-0 text-info" />
                    <span className="text-xs text-foreground">
                        <span className="font-medium text-info">
                            {pendingReviewCount === 1
                                ? '1 resultado pronto'
                                : `${pendingReviewCount} resultados prontos`}
                        </span>
                        {' - '}
                        <span className="text-muted-foreground">Revisar</span>
                    </span>
                </button>
            )}

            <div className="space-y-3">
                {instrumentEntryCard}

                <div>
                    <ResultSourceCard
                        icon={Send2}
                        title="Enviar instrumentos ao paciente"
                        // description={
                        //     <>
                        //         <p>Envie instrumentos para o paciente responder online.</p>
                        //         {!hasSelectedPatient && (
                        //             <p className="mt-2">
                        //                 Selecione um paciente para habilitar esta ação.
                        //             </p>
                        //         )}
                        //     </>
                        // }
                        showAIBadge
                        disabled={!hasSelectedPatient}
                        onClick={handleAssignInstruments}
                    >
                        {(sendTestAccess.isUnavailableInPlan && sendTestAccess.availableFromPlan) ||
                        (correctWithAiAccess.isUnavailableInPlan &&
                            correctWithAiAccess.availableFromPlan) ? (
                            <div className="absolute right-3 top-11 flex flex-col items-end gap-1">
                                {sendTestAccess.isUnavailableInPlan &&
                                sendTestAccess.availableFromPlan ? (
                                    <FeatureUnavailableChip
                                        availablePlan={sendTestAccess.availableFromPlan}
                                    />
                                ) : null}
                                {correctWithAiAccess.isUnavailableInPlan &&
                                correctWithAiAccess.availableFromPlan ? (
                                    <FeatureUnavailableChip
                                        availablePlan={correctWithAiAccess.availableFromPlan}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </ResultSourceCard>
                </div>

                {publisherResultImportsFeature.enabled && (
                    <ResultSourceCard
                        icon={ExportCurve}
                        title="Importar resultado de editora"
                        // description="Importe o PDF da editora para extrair os resultados."
                        showAIBadge
                        onClick={handleImportPublisherResults}
                        disabled={!hasSelectedPatient || isReadOnly}
                    />
                )}
            </div>

            {/* The battery already in the laudo. This is the half of the
                navigation that does not depend on finding anything on the
                page: every instrument is reachable from here, named, in
                document order. */}
            {batteryGroups.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground">
                        Instrumentos já adicionados
                    </h3>
                    {/* Collapsed, the list is a prefix of the battery, so a
                        drop index means the same thing in both — but an
                        instrument can only be dragged as far as the fold.
                        Expanding mid-drag is not an option: dnd measures the
                        rows on lift and inserting more of them lands the drop
                        somewhere else. */}
                    <Droppable
                        droppableId="battery_groups"
                        type="battery-group"
                        isDropDisabled={!canReorderBattery}
                    >
                        {(droppableProvided) => (
                            <div
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                                className="space-y-1.5"
                            >
                                {visibleGroups.map((group, index) => (
                                    <Draggable
                                        key={group.anchorRealId ?? group.firstRealId}
                                        draggableId={`battery-group-${group.anchorRealId ?? group.firstRealId}`}
                                        index={index}
                                        isDragDisabled={!canReorderBattery}
                                        // The handle is a <button>, and dnd
                                        // refuses to lift from an interactive
                                        // element unless told otherwise — which
                                        // silently costs the keyboard sensor
                                        // (space to lift, arrows to move).
                                        disableInteractiveElementBlocking
                                    >
                                        {(draggableProvided, snapshot) => (
                                            <div
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                className={cn(
                                                    'group/instrument flex w-full items-center rounded-lg border bg-card transition-colors',
                                                    snapshot.isDragging
                                                        ? 'border-primary/60 bg-surface shadow-md'
                                                        : 'border-border hover:bg-surface',
                                                )}
                                            >
                                                {canReorderBattery && (
                                                    <TooltipProvider delayDuration={400}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    {...draggableProvided.dragHandleProps}
                                                                    aria-label={`Reordenar ${group.label}`}
                                                                    className="flex w-6 shrink-0 cursor-grab items-center justify-center self-stretch rounded-l-lg text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:text-foreground active:cursor-grabbing group-hover/instrument:text-muted-foreground"
                                                                >
                                                                    <GripVertical className="size-4" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            {/* Left, not right: the panel hugs the
                                                                viewport edge. */}
                                                            <TooltipContent side="left">
                                                                Arraste para reordenar o instrumento
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenGroup(group)}
                                                    title={group.label}
                                                    className={cn(
                                                        'flex min-w-0 flex-1 items-center gap-2.5 py-2 pr-3 text-left',
                                                        !canReorderBattery && 'pl-3',
                                                    )}
                                                >
                                                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                                                        {group.label}
                                                    </span>
                                                    <ArrowRight2 className="size-3 shrink-0 text-muted-foreground" />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {droppableProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                    {batteryGroups.length > VISIBLE_GROUPS_COLLAPSED && (
                        <button
                            type="button"
                            onClick={() => setShowAllGroups((current) => !current)}
                            className="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {showAllGroups
                                ? 'Mostrar menos'
                                : `Mostrar mais ${batteryGroups.length - VISIBLE_GROUPS_COLLAPSED}`}
                            {showAllGroups ? (
                                <ArrowUp2 className="size-3" />
                            ) : (
                                <ArrowDown2 className="size-3" />
                            )}
                        </button>
                    )}
                </div>
            )}

            <div className="space-y-2">
                {/* The escape hatch: an empty report has no block to click, and
                    a divider or a standalone paragraph belongs to no group. */}
                <button
                    type="button"
                    onClick={() =>
                        setPanelScope({
                            anchorRealId: null,
                            label: UNSCOPED_GROUP_LABEL,
                            focusedRealId: null,
                        })
                    }
                    className="flex items-center gap-1 text-xs font-medium text-info transition-colors hover:text-info/80"
                >
                    Blocos livres
                    <ArrowRight2 className="size-3" />
                </button>
            </div>
        </div>
    );
}
